import moment from 'moment';
import { Platform } from 'react-native';
import encryptedStorage from '../stores/encryptedStorage';
import store from '../stores/store';
import utils from './utils';

export type Session = {
  startDate: number;
  endDate: number;
};

const storage = encryptedStorage();

let timer: ReturnType<typeof setTimeout> | null = null;
let startDate: number;

// interval to store the last date every second to detect if app was put into background
let intervalPromise: ReturnType<typeof setInterval>;
let lastDate: Date;

// Set max Idle time after that time tracking stops 1 min
let maxIdleTime = 60 * 1000;

export function touched() {
  if (!timer) {
    initInterval();
    startDate = new Date().valueOf();
    timer = setTimeout(timerOver, maxIdleTime);
  } else {
    clearTimeout(timer);
    timer = setTimeout(timerOver, maxIdleTime);
  }
}

export async function stop(): Promise<void> {
  return new Promise((resolve) => {
    // stop timer if its running and save session
    if (timer) {
      clearTimeout(timer);
      timerOver().then(function () {
        resolve();
      });
    } else {
      resolve();
    }
  });
}

function initInterval() {
  // init last Date with current time;
  lastDate = new Date();
  intervalPromise = setInterval(function () {
    let currentDate = new Date();
    let timeDifference = currentDate.getTime() - lastDate.getTime();
    // if time difference is more then 10 seconds stop tracking as the app was in background then
    // also check if time difference is negative (e.g. if user set his clock to the past and stop tracking then)
    if (timeDifference > 10000 || timeDifference < 0) {
      stop();
    } else {
      lastDate = new Date();
    }
  }, 1000);
}

async function timerOver() {
  timer = null;
  // cancel interval
  clearInterval(intervalPromise);
  return new Promise((resolve) => {
    resolve(addSession(startDate, lastDate));
  });
}

async function addSession(
  startDate: number,
  endDateObject: Date,
): Promise<void> {
  // If user was idle save the last duration exercised to continue from it
  if (!store.getState().resultModalIsShowing) {
    utils.setLocalTotalExerciseTime();
    utils.pushPracticeSessionTrackAndReset();
  }
  let userId = store.getState().user._id;
  let starContainerKey = 'starContainer' + userId;

  let endDate = endDateObject.valueOf();

  let uuid = startDate.toString();
  if (Platform.OS !== 'web') {
    uuid = '7561653' + '_' + startDate;
  }
  let newSession = {
    uuid: uuid,
    startDate: startDate,
    endDate: endDate,
  };

  let newSessionWithOffset = {
    uuid: uuid,
    startDate: startDate - timezoneOffset,
    endDate: endDate - timezoneOffset,
  };

  newSessionWithOffset.app = constants.appName;
  newSessionWithOffset.appVersion = constants.appVersion;
  if (Platform.OS !== 'web') {
    newSessionWithOffset.os = Platform.OS;
  } else {
    newSessionWithOffset.os = 'browser';
  }

  return new Promise((resolve) => {
    if (
      Object.prototype.hasOwnProperty.call(
        store.getState().starContainer,
        moment(endDate).format('YYYY-MM-DD'),
      )
    ) {
      let container =
        store.getState().starContainer[moment(endDate).format('YYYY-MM-DD')];
      container.duration = container.duration + getDuration(newSession);
      store.getActions().setStarContainer({
        starContainerKey,
        container,
      });
    } else {
      store.getActions().setStarContainer({
        starContainerKey,
        container: { duration: getDuration(newSession) },
      });
    }

    resolve();
  });
}

/* eslint-disable no-unused-vars */
export function getStarContainer() {
  const userId = store.getState().signedInUser?.user?._id;
  const starContainerKey = 'starContainer' + userId;
  const uniqueKey = `practiceSessions${userId}`;

  const storedStars = store.getState().storedStars?.[starContainerKey];

  if (!storedStars) {
    const container = {
      buffer: [],
      store: {},
      lastProgress: {
        date: moment().format('YYYY-MM-DD'),
        stars: 0,
      },
    };
    container.store = store.getState().storedSessions[uniqueKey];
    store.getActions().storeStars({ starContainerKey, container });
  }
}
/* eslint-disable no-unused-vars */
export function getStars(patientId: string) {
  return new Promise(async (res) => {
    let userId, container;
    if (patientId) {
      const key = `starContainer${patientId}`;
      const data = await storage.getItem(key);
      container = data;
    } else {
      userId = store.getState().signedInUser?.user?._id;
      container = store.getState().storedStars['starContainer' + userId];
    }
    if (!container) return res(null);
    const starData: Record<string, unknown> = {};
    const containerStore = container.store;
    const storeKeys = Object.keys(containerStore);
    storeKeys.forEach(function (key) {
      const value = containerStore[key].duration;
      let stars = msToStars(value);
      if (stars > 0) {
        starData[key] = stars;
      }
    });
    res(starData);
  });
}

function msToStars(ms: number) {
  let starTime = 1000 * 60 * 10;
  return Math.floor(ms / starTime);
}

function getDuration(session: Session) {
  return session.endDate - session.startDate;
}
